/**
 * Utilitários para RECEITAS parceladas.
 *
 * Para receitas parceladas, o campo `planned_amount` guarda o TOTAL de
 * todas as parcelas (ex.: R$ 2.500,00 em 5x). O valor que interessa a um
 * período orçamentário (PO) é o da parcela daquele período, não o total.
 *
 * ATENÇÃO: DESPESAS mensais parceladas guardam o valor MENSAL da parcela em
 * `planned_amount` (formulário "Valor mensal a pagar", migração legada e
 * totais/relatórios do backend) — para despesas, NÃO dividir.
 */

export interface InstallmentPlannedAmountParams {
  /** Valor planejado armazenado no lançamento (total, quando parcelado). */
  plannedAmount: number | string | null | undefined;
  /** Se o lançamento é do tipo parcelado. */
  isInstallment: boolean;
  /** Número da parcela do período atual (1-based). */
  currentInstallment?: number | null;
  /** Quantidade total de parcelas. */
  totalInstallments?: number | null;
}

/**
 * Devolve o valor planejado da parcela do período atual.
 *
 * Replica a lógica do backend (`services/installment_calculator.py`),
 * calculando em centavos para evitar erros de ponto flutuante:
 * - as (N-1) primeiras parcelas usam o piso (floor) da divisão;
 * - a última parcela recebe o restante, fechando o total exato.
 *
 * Para lançamentos não parcelados (ou sem dados de parcela suficientes),
 * devolve o próprio `planned_amount`.
 */
export function getInstallmentPlannedAmount(
  params: InstallmentPlannedAmountParams
): number {
  const planned = Number(params.plannedAmount || 0);
  if (!params.isInstallment) return planned;

  const totalInstallments = params.totalInstallments ?? 0;
  const currentInstallment = params.currentInstallment ?? 0;

  if (totalInstallments < 1 || currentInstallment < 1) return planned;

  const totalCents = Math.round(planned * 100);
  const base = Math.floor(totalCents / totalInstallments);
  const last = totalCents - base * (totalInstallments - 1);
  const cents = currentInstallment >= totalInstallments ? last : base;

  return cents / 100;
}
